import React from 'react';
import {
	DOMAIN_OPTIONS,
	WORKFLOW_INTENTION_OPTIONS,
} from 'src/components/createWorkflow/CreateWorkflowConstants';
import { useStore } from 'src/stores/store';
import { Store } from 'src/stores/store.interface';
import {
	Form,
	Container,
	FormField,
	Input,
	Header,
	SpaceBetween,
	Textarea,
	ColumnLayout,
	Multiselect,
	RadioGroup,
	AttributeEditor,
	Select,
	Checkbox,
} from '@amzn/awsui-components-console';
import { ApproverType } from '@amzn/snow-data-service-typescript-client';
import { WorkflowIntention } from '@amzn/snow-nxt-workflow-service-typescript-client';
import ResponsiveLabel from 'src/components/commonComponents/responsiveLabel/ResponsiveLabel';

const isUpdateWorkflow = (intention: WorkflowIntention): boolean =>
	intention === WorkflowIntention.UPDATE;

export default function CreateWorkflowMetadataStep() {
	const isReadOnly = useStore((state: Store) => state.isReadOnly);
	const workflowName = useStore((state: Store) => state.workflowName);
	const entityType = useStore((state: Store) => state.entityType);
	const description = useStore((state: Store) => state.workflowDescription);
	const selectedOptions = useStore(
		(state: Store) => state.selectedDomainOptions
	);
	const approvalRequiredToStartExecution = useStore((state: Store) => state.approvalRequiredToStartExecution);
	const batchExecutionRequired = useStore((state: Store) => state.batchExecutionRequired);
	const isReRunAllowed = useStore((state: Store) => state.isReRunAllowed);
	const usableAsSubWorkflow = useStore((state: Store) => state.usableAsSubWorkflow);
	const workflowIntention = useStore((state: Store) => state.workflowIntention);
	const setEntityType = useStore((state: Store) => state.setEntityType);
	const setIsApprovalRequiredToStartExecution = useStore((state: Store) => state.setIsApprovalRequiredToStartExecution);
	const setCanWorkflowAutotriggered = useStore((state: Store) => state.setCanWorkflowAutotriggered);
	const setApproversMetadata = useStore((state: Store) => state.setApproversMetadata);
	const updateWorkflowName = useStore((state: Store) => state.setWorkflowName);
	const updateDescription = useStore(
		(state: Store) => state.setWorkflowDescription
	);
	const setSelectedOptions = useStore(
		(state: Store) => state.setSelectedDomainOptions
	);
	const setWorkflowIntention = useStore(
		(state: Store) => state.setWorkflowIntention
	);
	const istheSubmitButtonClickedForCreateWF = useStore(
		(state: Store) => state.istheSubmitButtonClickedForCreateWF
	);
	const canWorkflowAutotriggered = useStore(
		(state: Store) => state.canWorkflowAutotriggered
	);
	const approversMetadata = useStore(
		(state: Store) => state.approversMetadata
	);
	const setIsBatchExecutionRequired = useStore((state: Store) => state.setIsBatchExecutionRequired);
	const setIsReRunAllowed = useStore((state: Store) => state.setIsReRunAllowed);
	const setUsableAsSubWorkflow = useStore((state: Store) => state.setUsableAsSubWorkflow);
	const isDomainEmpty =
		(!selectedOptions || selectedOptions.length === 0) &&
		istheSubmitButtonClickedForCreateWF;
	const isWorkflowNameEmpty =
		!workflowName && istheSubmitButtonClickedForCreateWF;
	const isDescriptionEmpty =
		!description && istheSubmitButtonClickedForCreateWF;
	const isEntityTypeEmpty = !entityType && istheSubmitButtonClickedForCreateWF;
	const isWorkflowIntentionEmpty =
		!workflowIntention && istheSubmitButtonClickedForCreateWF;

	const approvalDefinition = [
		{
			label: <ResponsiveLabel>Approver Alias</ResponsiveLabel>,
			control: (item, index) => (
				<Input
					value={item.approverAlias}
					onChange={(event) => {
						const updatedItems = [...approversMetadata];

						updatedItems[index] = {
							...updatedItems[index],
							approverAlias: event.detail.value,
						};
						setApproversMetadata(updatedItems);
					}}
				/>
			),
		},
		{
			label: <ResponsiveLabel>Approver Type</ResponsiveLabel>,
			control: (item, index) => (
				<Select
					selectedOption={item.approverType}
					onChange={(detail) => {
						const updatedItems = [...approversMetadata];

						updatedItems[index] = {
							...item,
							approverType: detail.detail.selectedOption,
						};
						setApproversMetadata(updatedItems);
					}}
					options={[
						{ value: ApproverType.LDAP },
						{ value: ApproverType.POSIX },
						{ value: ApproverType.USER },
					]}
				/>
			),
		},
		{
			label: <ResponsiveLabel>Number of Approvers</ResponsiveLabel>,
			control: (item, index) =>
				[...approversMetadata][index].approverType.value !== ApproverType.USER && (<Input
					value={item.numberOfApprovers}
					onChange={(event) => {
						if (!isNaN(Number(event.detail.value))) {
							const updatedItems = [...approversMetadata];

							updatedItems[index] = {
								...updatedItems[index],
								numberOfApprovers: event.detail.value,
							};
							setApproversMetadata(updatedItems);
						}
					}}
				/>
				),
			errorText: (item) =>
				(item?.approvalType?.value !== ApproverType.USER) && (+item.numberOfApprovers < 1 || +item.numberOfApprovers > 100) ? 'Number of approvers should be in the range [1,100]' : null,
		},
		{
			label: <ResponsiveLabel>Approver Level</ResponsiveLabel>,
			control: (item, index) =>
				[...approversMetadata][index].approverType.value !== ApproverType.USER && ( <Input
					value={item.level}
					onChange={(event) => {
						if (!isNaN(Number(event.detail.value))) {
							const updatedItems = [...approversMetadata];

							updatedItems[index] = {
								...updatedItems[index],
								level: event.detail.value,
							};
							setApproversMetadata(updatedItems);
						}
					}}
					placeholder="Enter a number between 1 to 100"
				/>
				),
			errorText: (item) =>
				item?.approvalType?.value !== ApproverType.USER && (+item.level < 1 || +item.level > 12) ? 'Approver Level should be in the range [1,12]' : null,
		}];

	return (
		<div>
			<Form>
				<SpaceBetween size="l">
					<Container header={<Header variant="h2">Basic Information</Header>}>
						<ColumnLayout>
							<div>
								<FormField
									label="Workflow Name"
									description="Enter the workflow name."
									data-testid="workflow_name"
									errorText={
										isWorkflowNameEmpty
											? 'Please fill in the workflow Name.'
											: ''
									}
								>
									<Input
										onChange={({ detail }) => {
											updateWorkflowName(detail.value);
										}}
										value={workflowName}
										disabled={isReadOnly}
									/>
								</FormField>
							</div>
							<div>
								<FormField
									label="Domain"
									description="Enter the domain for the workflow. Please note tasks will be displayed based on the selected domain"
									data-testid="domain_test"
									errorText={
										isDomainEmpty
											? 'Please select at least one Domain to proceed'
											: ''
									}
								>
									<Multiselect
										selectedOptions={selectedOptions}
										onChange={({ detail }) => {
											setSelectedOptions(detail.selectedOptions);
										}}
										options={DOMAIN_OPTIONS}
										disabled={isReadOnly}
									/>
								</FormField>
							</div>
							<div>
								<FormField
									label="Description"
									data-testid="workflow_description"
									description="Describe the usage of the workflow."
									errorText={
										isDescriptionEmpty
											? 'Please share the description for the workflow, for example where and how it will be used'
											: ''
									}
								>
									<Textarea
										onChange={({ detail }) => {
											updateDescription(detail.value);
										}}
										value={description}
										placeholder="Description placeholder"
										disabled={isReadOnly}
									/>
								</FormField>
							</div>
							<div>
								<FormField
									label="EntityType"
									data-testid="entityType"
									description="Please enter the type of the warehouse, process or machine"
									errorText={
										isEntityTypeEmpty ? 'Please fill in the entity Type' : ''
									}
								>
									<Input
										onChange={({ detail }) => {
											setEntityType(detail.value);
										}}
										value={entityType}
										disabled={isReadOnly}
									/>
								</FormField>
							</div>
							<div>
								<FormField
									label="Workflow Intention"
									data-testid="workflow_intention"
									description="Select the intention or purpose of this workflow"
									errorText={
										isWorkflowIntentionEmpty
											? 'Please select a workflow intention'
											: ''
									}
								>
									<Select
										selectedOption={
											WORKFLOW_INTENTION_OPTIONS.find(
												(opt) => opt.value === workflowIntention
											) || null
										}
										onChange={({ detail }) => {
											setWorkflowIntention(
												detail.selectedOption.value as WorkflowIntention
											);
										}}
										options={WORKFLOW_INTENTION_OPTIONS}
										disabled={isReadOnly}
									/>
								</FormField>
							</div>
							<div>
								<FormField
									label="Is batch execution required?"
									data-testid="is_batch_execution_required"
									description="Choose Yes, if this workflow require execution by grouping multiple entities into a single execution."
								>
									<RadioGroup
										value={String(batchExecutionRequired)}
										items={[
											{
												label: 'Yes',
												value: 'true',
												disabled: isReadOnly,
											},
											{
												label: 'No',
												value: 'false',
												disabled: isReadOnly,
											},
										]}
										onChange={({ detail }) =>
											setIsBatchExecutionRequired(detail.value === 'true')
										}
										readOnly={isReadOnly}
									/>
								</FormField>
							</div>
							{isUpdateWorkflow(workflowIntention) && (
								<div>
									<FormField
										label="Is re-run allowed?"
										data-testid="is_re_run_allowed"
										description="Choose Yes if this workflow allows re-running executions for the same entity. Choose No if each entity can only have one execution."
									>
										<RadioGroup
											value={String(isReRunAllowed)}
											items={[
												{
													label: 'Yes',
													value: 'true',
													disabled: isReadOnly,
												},
												{
													label: 'No',
													value: 'false',
													disabled: isReadOnly,
												},
											]}
											onChange={({ detail }) =>
												setIsReRunAllowed(detail.value === 'true')
											}
											readOnly={isReadOnly}
										/>
									</FormField>
								</div>
							)}
							<div>
								<FormField
									label="Can this workflow be used as a sub-workflow?"
									data-testid="is_usable_as_sub_workflow"
									description="Choose Yes to let other workflows embed this workflow as a step."
								>
									<RadioGroup
										value={String(usableAsSubWorkflow)}
										items={[
											{
												label: 'Yes',
												value: 'true',
												disabled: isReadOnly,
											},
											{
												label: 'No',
												value: 'false',
												disabled: isReadOnly,
											},
										]}
										onChange={({ detail }) =>
											setUsableAsSubWorkflow(detail.value === 'true')
										}
										readOnly={isReadOnly}
									/>
								</FormField>
							</div>
							<div>
								<FormField
									label="Is approval required to start execution?"
									data-testid="is_approval_required"
									description="Choose Yes, if approval is required for the information submitted for a warehouse/machine before starting the workflow execution"
								>
									<RadioGroup
										value={String(approvalRequiredToStartExecution)}
										items={[
											{
												label: 'Yes',
												value: 'true',
												disabled: isReadOnly,
											},
											{
												label: 'No',
												value: 'false',
												disabled: isReadOnly,
											},
										]}
										onChange={({ detail }) =>
											setIsApprovalRequiredToStartExecution(detail.value === 'true')
										}
										readOnly={isReadOnly}
									/>
								</FormField>
							</div>

							{approvalRequiredToStartExecution && (
								<FormField
									label="Approvers"
									data-testid="approvers_details"
									description="Provide the details about the approvers."
								>
									<AttributeEditor
										onAddButtonClick={() =>
											setApproversMetadata([
												...approversMetadata,
												{
													approverAlias: '',
													approverType: { value: ApproverType.POSIX },
													numberOfApprovers: '1',
													level: '1',
												},
											])
										}
										onRemoveButtonClick={({ detail: { itemIndex } }) => {
											const tmpItems = [...approversMetadata];

											tmpItems.splice(itemIndex, 1);
											setApproversMetadata(tmpItems);
										}}
										items={approversMetadata}
										addButtonText="Add new approver"
										removeButtonText="Remove"
										definition={hasPrincipalApprovers(approversMetadata) ?
											approvalDefinition : [approvalDefinition[0], approvalDefinition[1]]}
									/>
								</FormField>
							)}
							{approvalRequiredToStartExecution && (
								<FormField
									label="Workflow Autotrigger"
									data-testid="AutoTrigger"
									description="If this option is selected, the workflow will start immediately after approval. If not, you will need to trigger it manually"
								>
									<Checkbox
										onChange={({ detail }) =>
											setCanWorkflowAutotriggered(detail.checked)
										}
										checked={canWorkflowAutotriggered}
										disabled
									>
										Workflow Autotrigger
									</Checkbox>
								</FormField>
							)}
						</ColumnLayout>
					</Container>
				</SpaceBetween>
			</Form>
		</div>
	);
}

const hasPrincipalApprovers = (approversMetadata): boolean => approversMetadata.some(approver => (approver?.approvalType?.value) !== ApproverType.USER);
